import type { ProductHit } from '../../../types/algolia';
import type { CTAVariantProps } from '../../shared/core/button/CTA';
import type { Hit } from 'instantsearch.js';
import type { InfiniteHitsCache } from 'instantsearch.js/es/connectors/infinite-hits/connectInfiniteHits';
import type { InsightsMethodMap } from 'search-insights';
import type { RuleSet } from 'styled-components';

import { createInfiniteHitsSessionStorageCache } from 'instantsearch.js/es/lib/infiniteHitsCache';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useCurrentRefinements, useInfiniteHits, useSortBy, useStats } from 'react-instantsearch';
import styled, { css } from 'styled-components';

import { FocusOrderManager } from './FocusOrderManager';
import { PLPItemListSchema } from './PLPItemListSchema';
import { ProductGrid } from './ProductGrid';
import { ProductTile } from './ProductTile';
import { SkeletonGridTiles } from './SkeletonGridTiles';
import { useCustomPagination } from './useCustomPagination';
import { useManagedProductHits } from './useManagedProductHits';
import { CustomEventTypes } from '../../../utilities/constants';
import { MAX_NUMBER_OF_VIEWED_IDS_PER_EVENT } from '../../../utilities/constants/algolia';
import {
  PRODUCT_LIST_NEXT_RESULTS_VARIABLE,
  PRODUCT_LIST_SEEN_RESULTS_VARIABLE,
  PRODUCT_LIST_TOTAL_RESULTS_VARIABLE,
} from '../../../utilities/constants/amplienceVariables';
import { useDataLayerContext } from '../../../utilities/context/static/DataLayerContext';
import { useProductListContext } from '../../../utilities/context/static/ProductListContext';
import { useStaticContext } from '../../../utilities/context/static/StaticContext';
import { useAlgoliaLoadingState } from '../../../utilities/hooks/search/useAlgoliaLoadingState';
import { useAlgoliaStyleVariants } from '../../../utilities/hooks/search/useAlgoliaStyleVariants';
import { parseInternalSearchDataLayer } from '../../../utilities/vendors';
import { Skeleton } from '../../shared/common/SkeletonPlaceholder';
import { shimmerAnimation } from '../../shared/common/styles';
import { CTA } from '../../shared/core/button/CTA';
import { Typography } from '../../shared/core/typography/Typography';
import { GenericErrorBoundary } from '../../shared/error/GenericErrorBoundary';
import { useAlgoliaInsights } from '~/components/shared/vendors/useAlgoliaInsights';
import { PLP_NAVIGATED_PRODUCT } from '~/utilities/constants/localStorageKeys';
import { useConsentContext } from '~/utilities/context/dynamic/ConsentContext';
import { useAppContext } from '~/utilities/context/static/AppContext';
import { SiteType } from '~/utilities/graphql/codegen';
import { chunks } from '~/utilities/helpers';
import { uniqBy } from '~/utilities/helpers/uniqBy';
import { useAlgoliaSortingOptions } from '~/utilities/hooks/search/useAlgoliaSortingOptions';

const sessionStorageCache =
  createInfiniteHitsSessionStorageCache() as unknown as InfiniteHitsCache<ProductHit>;

const styles = {
  GStarGridFooter: css`
    padding: 32px 0 64px;
  `,
  AafGridFooter: css`
    padding: 64px 0;
  `,

  GStarSeenProductsAmount: css`
    ${({ theme }) => css`
      color: ${theme.colors.content.secondary};
      font-size: ${theme.font.size[3]};
      font-weight: ${theme.variables.font.weight.regular};
      letter-spacing: 0.01em;
      line-height: 1.4;
    `}
  `,
  AafSeenProductsAmount: css`
    font-size: 14px;
    letter-spacing: 0.01em;
    color: #252523;
  `,

  GstarGridHeader: css`
    padding: ${({ theme }) => theme.padding.sm} 0 32px;
  `,
  AafGridHeader: css`
    padding: 32px 0;
  `,
};

const GridFooterStyles: Record<SiteType, RuleSet<object>> = {
  [SiteType.Gstar]: styles.GStarGridFooter,
  [SiteType.Wahts]: styles.GStarGridFooter,
  [SiteType.Outlet]: styles.GStarGridFooter,
  [SiteType.Employeeshop]: styles.GStarGridFooter,
  [SiteType.Aaf]: styles.AafGridFooter,
};

const SeenProductsAmountStyles: Record<SiteType, RuleSet<object>> = {
  [SiteType.Gstar]: styles.GStarSeenProductsAmount,
  [SiteType.Wahts]: styles.GStarSeenProductsAmount,
  [SiteType.Outlet]: styles.GStarSeenProductsAmount,
  [SiteType.Employeeshop]: styles.GStarSeenProductsAmount,
  [SiteType.Aaf]: styles.AafSeenProductsAmount,
};

const GridHeaderStyles: Record<SiteType, RuleSet<object>> = {
  [SiteType.Gstar]: styles.GstarGridHeader,
  [SiteType.Wahts]: styles.GstarGridHeader,
  [SiteType.Outlet]: styles.GstarGridHeader,
  [SiteType.Employeeshop]: styles.GstarGridHeader,
  [SiteType.Aaf]: styles.AafGridHeader,
};

const S = {
  GridFooter: styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    ${({ theme }) => GridFooterStyles[theme.siteType]}
  `,
  GridHeader: styled.div`
    ${({ theme }) => css`
      display: flex;
      flex-direction: column;
      align-items: center;
      ${GridHeaderStyles[theme.siteType]}
    `}
  `,
  Button: styled(CTA)`
    position: relative;

    /* safari fix (: */
    &::after {
      content: ' ';
      display: none;
      position: absolute;
      inset: 0;
      pointer-events: none;
    }

    &[data-disabled='true'] {
      ${shimmerAnimation}
    }
  `,
  SeenProductsAmount: styled(Typography)`
    ${({ theme }) => SeenProductsAmountStyles[theme.siteType]}
  `,
};

const TIMEOUT_DURATION_MS = 5000;

export const InfiniteProductHits = () => {
  const { items: appliedFilters } = useCurrentRefinements();
  const { primaryIndex, sortingOptions } = useAlgoliaSortingOptions();
  const { currentRefinement: appliedSorting } = useSortBy({ items: sortingOptions });
  const { isGStar, siteType } = useAppContext();
  const { contentSize = 0, categoryDetails, systemTexts, isPLP } = useProductListContext();
  const { nbHits, hitsPerPage = 0, query, page } = useStats();
  const managedHits = useManagedProductHits();
  const { shouldReportAnalytics } = useConsentContext();
  const { isFirstLoading, isLoading } = useAlgoliaLoadingState();
  const { pushToAlgoliaWithQueryId } = useAlgoliaInsights();
  const [isPreviousLoading, setIsPreviousLoading] = useState(false);
  const { configuration: { enableExponea, appStoreId } = {} } = useStaticContext();
  const { pushToDataLayer } = useDataLayerContext();
  const { nextHref, prevHref, prevPage, setCurrentPage } = useCustomPagination();
  const currentHitsIDList = useRef<string[]>([]);
  const previousHitsLengthRef = useRef<number>(0);
  const shouldRestoreFocusRef = useRef<boolean>(false);
  const gridContainerRef = useRef<HTMLDivElement>(null);

  const PaginationVariantProps: Record<SiteType, CTAVariantProps> = {
    [SiteType.Gstar]: { variant: 'outline', colorway: 'dark', size: 'md' },
    [SiteType.Employeeshop]: { variant: 'outline', colorway: 'dark', size: 'md' },
    [SiteType.Outlet]: { variant: 'legacy', size: 'md' },
    [SiteType.Aaf]: { variant: 'outline', colorway: 'dark', size: 'lg' },
    [SiteType.Wahts]: { variant: 'outline', colorway: 'dark', size: 'md' },
  };

  const {
    items: productHits,
    isFirstPage,
    isLastPage,
    results,
    showMore,
  } = useInfiniteHits<ProductHit>({
    cache: sessionStorageCache,
  });

  const shouldPersonalize = useMemo(() => {
    const isSorted = primaryIndex !== appliedSorting;
    const hasAppliedFacets = !!appliedFilters.length;

    return isFirstPage && !!managedHits.length && !isSorted && !hasAppliedFacets;
  }, [appliedFilters, appliedSorting, isFirstPage, primaryIndex, managedHits]);

  const hits = useMemo(() => {
    let finalHits = productHits;

    if (shouldPersonalize) {
      finalHits = uniqBy([...managedHits, ...productHits], 'objectID').map((hit, index) => ({
        ...hit,
        // remap the position of each hit adjusted after personalization
        // required for analytics
        __position: index + 1,
      }));
    }

    finalHits = finalHits.filter(hit => !!hit['baseProduct.code']);

    if (!contentSize) {
      return finalHits;
    }

    // this logic is required to maintain a complete row of products on the last row in case when there is content injection
    // on the last page of PLP it is required to render all available products regardless of content injection
    return isFirstPage && !isLastPage ? finalHits.slice(0, contentSize * -1) : finalHits;
  }, [contentSize, isFirstPage, isLastPage, productHits, managedHits, shouldPersonalize]);

  const styleCodes = useMemo(() => {
    if (!isGStar) {
      return [];
    }

    const startPosition = page * hitsPerPage - (page === 0 ? 0 : contentSize);
    const endPosition = (page + 1) * hitsPerPage - (page === 0 ? contentSize : 0);

    const codes = hits.slice(startPosition, endPosition).map(hit => hit.styleCode);

    return uniqBy(codes, code => code);
  }, [isGStar, contentSize, hits, page, hitsPerPage]);

  const styleVariants = useAlgoliaStyleVariants({ styleCodes });

  const showNextCount = Math.abs(Math.min(nbHits - hits.length, hitsPerPage));

  const seenText = (systemTexts?.productList?.seenResults || '')
    .replace(PRODUCT_LIST_SEEN_RESULTS_VARIABLE, `${hits.length}`)
    .replace(PRODUCT_LIST_TOTAL_RESULTS_VARIABLE, `${nbHits}`);

  const showNextText = (systemTexts?.productList?.showNext || '').replace(
    PRODUCT_LIST_NEXT_RESULTS_VARIABLE,
    `${showNextCount}`
  );

  const showPreviousText = systemTexts?.productList?.showPrevious || '';
  const anyLoading = isFirstLoading || isLoading;

  const initialSkeletonNumber = nbHits === 0 ? hitsPerPage : Math.min(hitsPerPage, nbHits);

  useEffect(() => {
    setIsPreviousLoading(false);
    localStorage.setItem('algoliaQueryID', results?.queryID || '');
  }, [hits.length, results?.queryID]);

  /**
   * focus element when navigating back to plp/srp from pdp as per specs
   */
  useEffect(() => {
    if (isFirstLoading) {
      return;
    }

    const productCode = sessionStorage.getItem(PLP_NAVIGATED_PRODUCT);

    if (!productCode) {
      return;
    }

    const element = document.querySelector<HTMLElement>(`[data-product-code="${productCode}"]`);

    sessionStorage?.removeItem(PLP_NAVIGATED_PRODUCT);

    if (!element) {
      return;
    }

    requestAnimationFrame(() => {
      // focus will scroll into view automatically
      // focus ring will be visible if navigation was done via keyboard
      element.focus();
    });
  }, [isFirstLoading]);

  useEffect(() => {
    const canDispatchViewEvents =
      hits.length &&
      currentHitsIDList.current.length !== hits.length &&
      !isLoading &&
      !isFirstLoading &&
      shouldReportAnalytics;

    if (!canDispatchViewEvents) {
      return;
    }

    const productIds = hits.map(hit => hit['baseProduct.code'].toLowerCase());
    const newItems = productIds.filter(id => !currentHitsIDList.current.includes(id));

    currentHitsIDList.current = productIds;

    if (query && !isPLP) {
      pushToDataLayer(parseInternalSearchDataLayer('page loaded', query, `${hits.length}`));
    }

    if (results?.queryID && newItems.length) {
      const hitsForAnalytics = hits.filter(hit =>
        newItems.includes(hit['baseProduct.code'].toLowerCase())
      );
      const event: keyof InsightsMethodMap = 'viewedObjectIDs';
      const eventName = `${isPLP ? 'PLP' : 'PRP'}_viewedObjectIDs`;

      // group hits by queryID to correctly report analytics to algolia because it is "queryID sensitive"
      // each product must have it's own queryID if it is comming from a different request
      const dispatchChunkedAlgoliaAnalytics: (
        value: [string, Hit<ProductHit>[] | undefined]
      ) => void = ([queryID, hits = []]) => {
        // algolia can handle only 20 objectIDs per event (???)
        [...chunks(hits, MAX_NUMBER_OF_VIEWED_IDS_PER_EVENT)].forEach(chunk => {
          // SONAR complaining about function depth
          const objectIDs = chunk.map(item => item.objectID); // NOSONAR

          pushToAlgoliaWithQueryId(event, eventName, {
            objectIDs,
            queryID,
          });
        });
      };

      Object.entries(Object.groupBy(hitsForAnalytics, hit => hit.__queryID!)).forEach(
        dispatchChunkedAlgoliaAnalytics
      );
    }

    localStorage.setItem('algoliaQueryID', results?.queryID || '');

    if (enableExponea) {
      const categoryProps = isPLP
        ? {
            categories_path: categoryDetails?.plpCategoryPath?.replace('/', ' > '),
            category_id: categoryDetails?.plpCategoryPathIds,
            categories_ids: [categoryDetails?.categoryCode],
          }
        : {};
      const eventProperties = Object.fromEntries(
        Object.entries({
          ...categoryProps,
          category_listed_products: newItems,
          category_name: isPLP ? categoryDetails?.categoryName : 'search',
          search_query: isPLP ? '' : query,
          page_number: page + 1,
          timestamp: Math.round(Date.now() / 1e3),
          domain: window.location.origin,
        }).filter(([, value]) => value)
      );

      pushToDataLayer({
        event_name: isPLP ? 'view_category' : 'search',
        namespace: 'exponea',
        event_properties: eventProperties,
      });
    }
  }, [
    enableExponea,
    hits,
    isLoading,
    isFirstLoading,
    isPLP,
    page,
    pushToDataLayer,
    pushToAlgoliaWithQueryId,
    query,
    appStoreId,
    categoryDetails,
    results?.queryID,
    shouldReportAnalytics,
  ]);

  useEffect(() => {
    document.dispatchEvent(new CustomEvent(CustomEventTypes.PLP_STATE_UPDATE));
  }, [hits]);

  /**
   * Restore focus to the first newly loaded productTile after clicking "Show More"
   * This ensures keyboard navigation continues smoothly through the new productTile
   */
  useEffect(() => {
    if (!shouldRestoreFocusRef.current || isLoading || !gridContainerRef.current) {
      return;
    }

    const previousLength = previousHitsLengthRef.current;
    const targetProductCode = hits[previousLength]?.code;

    if (!targetProductCode) {
      shouldRestoreFocusRef.current = false;

      return;
    }

    const observer = new MutationObserver(() => {
      const element = document.querySelector<HTMLElement>(
        `[data-product-code="${targetProductCode}"]`
      );

      if (element) {
        element.focus();
        shouldRestoreFocusRef.current = false;
        observer.disconnect();
      }
    });

    observer.observe(gridContainerRef.current, {
      childList: true,
      subtree: true,
    });

    const timeoutId = setTimeout(() => {
      observer.disconnect();
      shouldRestoreFocusRef.current = false;
    }, TIMEOUT_DURATION_MS);

    // eslint-disable-next-line consistent-return
    return () => {
      observer.disconnect();
      clearTimeout(timeoutId);
    };
  }, [hits, isLoading]);

  return (
    <div>
      {isPLP && isFirstPage && !isFirstLoading && hits.length > 0 && (
        <PLPItemListSchema hits={hits.slice(0, hitsPerPage)} />
      )}

      {!isFirstPage ? (
        <S.GridHeader>
          <S.Button
            href={anyLoading ? undefined : prevHref}
            rel="prev"
            scroll={false}
            shallow
            aria-busy={anyLoading}
            aria-disabled={anyLoading}
            data-disabled={anyLoading}
            data-testid="productList-showPrevious"
            label={showPreviousText}
            onClick={() => {
              setCurrentPage(Math.max(prevPage - 1, 0));
              setIsPreviousLoading(true);
            }}
            {...PaginationVariantProps[siteType]}
          />
        </S.GridHeader>
      ) : (
        <></>
      )}

      <ProductGrid data-testid="plp-grid" ref={gridContainerRef}>
        {/* Skeleton for loading previous results */}
        {isPreviousLoading && isLoading ? (
          <SkeletonGridTiles numberOfSkeletonTiles={hitsPerPage} />
        ) : (
          <></>
        )}

        {/* Skeleton for loading initial results on page load */}
        {isFirstLoading && <SkeletonGridTiles numberOfSkeletonTiles={initialSkeletonNumber} />}

        {/* Render products with managed focus order on all PLP pages */}
        {!isFirstLoading && isPLP && (
          <FocusOrderManager
            hits={hits}
            renderProductTile={hit => (
              <GenericErrorBoundary key={hit.objectID} fallback={<></>}>
                <ProductTile variants={styleVariants?.[hit.styleCode] || []} hit={hit} />
              </GenericErrorBoundary>
            )}
          >
            {orderedItems => orderedItems.map(item => item.component)}
          </FocusOrderManager>
        )}

        {/* Render products without focus manager, for SRP */}
        {!isFirstLoading && !isPLP && (
          <>
            {hits.map(hit => (
              <GenericErrorBoundary key={hit.objectID} fallback={<></>}>
                <ProductTile variants={styleVariants?.[hit.styleCode] || []} hit={hit} />
              </GenericErrorBoundary>
            ))}
          </>
        )}

        {/* Skeleton for loading next results */}
        {isLoading ? <SkeletonGridTiles numberOfSkeletonTiles={showNextCount} /> : <></>}
      </ProductGrid>

      <S.GridFooter>
        <Skeleton when={isFirstLoading} width="250px" height="20px">
          <S.SeenProductsAmount variant="body" component="div">
            {seenText}
          </S.SeenProductsAmount>
        </Skeleton>

        {!isLastPage ? (
          <S.Button
            href={anyLoading ? undefined : nextHref}
            rel="next"
            scroll={false}
            shallow
            aria-busy={anyLoading}
            aria-disabled={anyLoading}
            data-disabled={anyLoading}
            data-testid="productList-showNext"
            label={showNextText}
            onClick={() => {
              previousHitsLengthRef.current = hits.length;
              shouldRestoreFocusRef.current = true;
              showMore();
            }}
            {...PaginationVariantProps[siteType]}
          />
        ) : (
          <></>
        )}
      </S.GridFooter>
    </div>
  );
};
